import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getDownloadURL,
  getMetadata,
  getStorage,
  listAll,
  ref,
} from "firebase/storage";
import { getPricingPlans as getSimplePricingPlans } from "@/apis/rental/city-apis";
import {
  type FinishRentalDto,
  finishRental,
  getFinallyDataOfRental,
  getRentalById,
  getRentalImageDirectoryData,
  getRentalPreviewCost,
  getRentalStatuses,
  getRentals,
  getRentalsList,
  type RentalListQueryParams,
  searchUsersForRental,
  simulateRental,
  updateRental,
} from "@/apis/rental/rental-apis";
import type { Rental, RentalListParams } from "@/domain/rental";
import { constructImageDirectory } from "@/utils/utils";
import { queryKeys } from "./keys";

export function useRentals(
  params: RentalListQueryParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.rentals.list(params),
    queryFn: () => getRentalsList(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

export function useRental(id: number | null) {
  return useQuery({
    queryKey: queryKeys.rentals.detail(id ?? 0),
    // Query is disabled when id is null, so queryFn won't run with fallback value
    queryFn: () => getRentalById(id ?? 0),
    enabled: Boolean(id),
  });
}

export function useUpdateRental() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: Partial<Rental> }) =>
      updateRental(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.detail(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentals.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useFinishRental() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: FinishRentalDto }) =>
      finishRental(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.detail(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentals.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useRentalStatuses(enabled = true) {
  return useQuery({
    queryKey: queryKeys.enums.rentalStatuses,
    queryFn: getRentalStatuses,
    enabled,
    staleTime: Infinity,
  });
}

export function useSearchUsersForRental(search: string) {
  return useQuery({
    queryKey: queryKeys.rentals.userSearch(search),
    queryFn: () => searchUsersForRental(search),
    enabled: search.length >= 2,
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useRentalsGallery(params: RentalListParams) {
  return useQuery({
    queryKey: queryKeys.rentals.gallery(params),
    queryFn: () => getRentals(params),
    enabled: Boolean(params.cityId),
    placeholderData: keepPreviousData,
  });
}

export function useGalleryPricingPlans(cityId: number | null) {
  return useQuery({
    queryKey: queryKeys.cities.galleryPricingPlans(cityId ?? 0),
    // Query is disabled when cityId is null, so queryFn won't run with fallback value
    queryFn: () => getSimplePricingPlans(cityId ?? 0),
    enabled: Boolean(cityId),
  });
}

export function useSimulateRental() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      vehicleCode,
      cityId,
      stationCode,
    }: {
      vehicleCode: number;
      cityId: number;
      stationCode?: number;
    }) => simulateRental(vehicleCode, cityId, stationCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all });
    },
  });
}

export function useRentalState(rentalId: number | null) {
  return useQuery({
    queryKey: [...queryKeys.rentals.all, "state", rentalId] as const,
    // Query is disabled when rentalId is null, so queryFn won't run with fallback value
    queryFn: () => getFinallyDataOfRental(rentalId ?? 0),
    enabled: Boolean(rentalId),
  });
}

export function useRentalPreviewCost(
  rentalId: number | null,
  endTime?: string
) {
  return useQuery({
    queryKey: [
      ...queryKeys.rentals.all,
      "preview-cost",
      rentalId,
      endTime ?? null,
    ] as const,
    queryFn: () => getRentalPreviewCost(rentalId ?? 0, endTime),
    enabled: Boolean(rentalId),
  });
}

/**
 * Error class for rental image loading failures.
 */
export class RentalImageError extends Error {
  constructor(
    message: string,
    public readonly code: "NO_IMAGES" | "LOAD_FAILED"
  ) {
    super(message);
    this.name = "RentalImageError";
  }
}

/**
 * Fetches rental images from Firebase storage.
 *
 * This mutation hook:
 * 1. Fetches directory data from the backend API
 * 2. Lists all images in the Firebase storage path
 * 3. Returns download URLs with metadata for each image
 *
 * @returns A mutation hook with `mutate(rentalId)` to trigger image fetching
 *
 * @example
 * ```tsx
 * const { mutate: loadImages, isPending, isError, error } = useRentalImages()
 *
 * const handleOpenImages = (rentalId: number) => {
 *   loadImages(rentalId, {
 *     onSuccess: (urls) => {
 *       setPreviewImageUrls(urls)
 *       setImagePreviewModal(true)
 *     },
 *     onError: (error) => {
 *       if (error instanceof RentalImageError && error.code === 'NO_IMAGES') {
 *         showToast('No images found', 'error')
 *       }
 *     }
 *   })
 * }
 * ```
 */
export function useRentalsExport() {
  return useMutation({
    mutationFn: async (params: RentalListQueryParams): Promise<Rental[]> => {
      const response = await getRentalsList(params);
      return response.content || [];
    },
  });
}

export function useRentalImages() {
  return useMutation({
    mutationFn: async (rentalId: number): Promise<string[]> => {
      // Step 1: Get directory data from backend
      const directoryData = await getRentalImageDirectoryData(rentalId);

      // Step 2: Construct Firebase path and list images
      const storage = getStorage();
      const pathReference = ref(
        storage,
        constructImageDirectory(directoryData)
      );
      const result = await listAll(pathReference);

      if (result.items.length === 0) {
        throw new RentalImageError(
          "No images found for this rental",
          "NO_IMAGES"
        );
      }

      // Step 3: Get download URLs with metadata
      const urlPromises = result.items.map(async (itemRef) => {
        const [meta, url] = await Promise.all([
          getMetadata(itemRef),
          getDownloadURL(itemRef),
        ]);
        return `${url}&timeCreated=${meta.timeCreated}`;
      });

      return Promise.all(urlPromises);
    },
  });
}
